// frontend_react/src/services/api.js

import axios from "axios";
import { getCookie } from "../utils/cookies";

function getCsrfToken(kind) {
  const name = kind === "refresh" ? "csrf_refresh_token" : "csrf_access_token";

  const fromCookie = getCookie(name);
  if (fromCookie) return decodeURIComponent(fromCookie);

  const fromStorage = localStorage.getItem(name);
  if (fromStorage) return decodeURIComponent(fromStorage);

  return "";
}

const api = axios.create({
  baseURL: import.meta.env.VITE_BACKEND_URL || "/api",
  withCredentials: true,
});

let refreshing = false;
let queue = [];

function processQueue(error = null) {
  queue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });

  queue = [];
}

api.interceptors.response.use(
  (response) => {
    const access = getCookie("csrf_access_token");
    if (access) {
      localStorage.setItem("csrf_access_token", decodeURIComponent(access));
    }

    const refresh = getCookie("csrf_refresh_token");
    if (refresh) {
      localStorage.setItem("csrf_refresh_token", decodeURIComponent(refresh));
    }

    return response;
  },
  async (error) => {
    const { response, config } = error || {};

    const canRefresh =
      response?.status === 401 &&
      config &&
      !config._retry &&
      document.cookie.includes("refresh_token_cookie") &&
      (localStorage.getItem("csrf_refresh_token") ||
        getCookie("csrf_refresh_token"));

    if (!canRefresh) {
      return Promise.reject(error);
    }

    if (refreshing) {
      return new Promise((resolve, reject) => {
        queue.push({
          resolve: () => resolve(api(config)),
          reject,
        });
      });
    }

    config._retry = true;
    refreshing = true;

    try {
      const csrfRefresh = getCsrfToken("refresh");

      await api.post("/auth/refresh", null, {
        headers: {
          "X-CSRF-TOKEN": csrfRefresh,
        },
      });

      processQueue();
      return api(config);
    } catch (refreshError) {
      processQueue(refreshError);
      window.location.href = "/";
      throw refreshError;
    } finally {
      refreshing = false;
    }
  }
);

api.interceptors.request.use((config) => {
  config.withCredentials = true;

  const isFormData =
    typeof FormData !== "undefined" && config.data instanceof FormData;

  if (isFormData && config.headers) {
    delete config.headers["Content-Type"];
  }

  const url = config.url || "";
  const method = (config.method || "get").toLowerCase();
  const isRefresh = url.includes("/auth/refresh");

  if (["post", "put", "patch", "delete"].includes(method)) {
    const csrf = getCsrfToken(isRefresh ? "refresh" : "access");

    if (csrf) {
      config.headers["X-CSRF-TOKEN"] = csrf;
    }
  }

  return config;
});

export function buildCompanyScopeHeaders(selectedCompanyUuid) {
  if (!selectedCompanyUuid) return {};

  return {
    "X-Company-UUID": selectedCompanyUuid,
  };
}

export function buildActivityScopeHeaders(activityUuid) {
  if (!activityUuid) return {};

  return {
    "X-Activity-UUID": activityUuid,
  };
}


export default api;